/**
 * Numbers and addresses the Mac found in the agent's address book for people
 * in Reppr with an empty phone or email, waiting for a yes.
 *
 * contact_suggestions can be read by its owner but written only by the
 * server, so accepting and skipping go through /api/capture/contact-suggestions.
 */

import { pb } from '../lib/pocketbase'

export type SuggestionField = 'phone' | 'mobile' | 'email' | 'parent_phone' | 'parent_email'

export type ContactSuggestionItem = {
  id: string
  collection: 'clients' | 'prospects' | 'contacts' | 'client_users'
  target_id: string
  field: SuggestionField
  value: string
  display_value: string
  /** As the card labels it: "mobile", "work". */
  label: string | null
  /** The name on the address-book card. */
  card_name: string
  person: { role: 'self' | 'parent' | 'portal'; name: string; entity_type: 'client' | 'prospect' | 'contact'; entity_id: string }
  /** Records in Reppr that already have this number or address. */
  also_on: Array<string | null>
}

async function call(body?: Record<string, unknown>) {
  const res = await fetch('/api/capture/contact-suggestions', {
    method: body ? 'POST' : 'GET',
    headers: {
      ...(body ? { 'Content-Type': 'application/json' } : {}),
      Authorization: `Bearer ${pb.authStore.token}`,
    },
    body: body ? JSON.stringify(body) : undefined,
  })
  const text = await res.text()
  let data: Record<string, any>
  try {
    data = JSON.parse(text)
  } catch {
    throw new Error(
      `Contact suggestions returned ${res.status} but not JSON. If you are running the dev server on its own, this endpoint only exists under \`vercel dev\`.`,
    )
  }
  if (!res.ok) throw new Error(data.error || `Could not reach contact suggestions (${res.status}).`)
  return data
}

export async function listContactSuggestions(): Promise<ContactSuggestionItem[]> {
  const data = await call()
  if (!Array.isArray(data.items)) throw new Error('Contact suggestions did not return a list.')
  return data.items as ContactSuggestionItem[]
}

export async function acceptContactSuggestion(id: string) {
  return call({ action: 'accept', suggestion_id: id }) as Promise<{ collection: string; id: string; field: SuggestionField; value: string }>
}

export async function skipContactSuggestion(id: string) {
  return call({ action: 'skip', suggestion_id: id }) as Promise<{ id: string; status: 'skipped' }>
}
